// SPDX-License-Identifier: AGPL-3.0-only

import { defineStore } from "pinia";
import {
  loadSession,
  login,
  logout,
  type AuthenticatedSession,
} from "../api/authentication.js";
import { useUserSettingsStore } from "./user-settings.js";

export type AuthenticationStatus = "idle" | "loading" | "authenticated" | "anonymous";

interface AuthenticationState {
  status: AuthenticationStatus;
  session: AuthenticatedSession | null;
}

export const useAuthenticationStore = defineStore("authentication", {
  state: (): AuthenticationState => ({
    status: "idle",
    session: null,
  }),
  getters: {
    isAuthenticated: (state: AuthenticationState): boolean => state.status === "authenticated",
  },
  actions: {
    async initialize(force: boolean = false): Promise<void> {
      if (!force && this.status !== "idle") {
        return;
      }

      this.status = "loading";
      try {
        this.session = await loadSession();
        this.status = this.session === null ? "anonymous" : "authenticated";
      } catch (error: unknown) {
        this.status = "anonymous";
        throw error;
      }
    },
    async login(email: string, password: string): Promise<void> {
      this.session = await login(email, password);
      this.status = "authenticated";
    },
    async logout(): Promise<void> {
      try {
        await logout();
      } finally {
        useUserSettingsStore().reset();
        this.session = null;
        this.status = "anonymous";
      }
    },
  },
});
